import { Capacitor, registerPlugin } from '@capacitor/core'
import { getVoiceCallSession, isTerminalCallPhase, subscribeVoiceCallSession } from '../stores/voice-call-store'
import {
  normalizeRingerState,
  UNKNOWN_RINGER_STATE,
  type RingerState,
} from '../utils/ringer-policy'

// Not `utils/platform`: that pulls in `config/env` (import.meta), which would
// make every importer of the audio stack untestable under jest.
function getNativePlatform(): 'ios' | 'android' | 'web' {
  const platform = Capacitor.getPlatform()
  return platform === 'ios' || platform === 'android' ? platform : 'web'
}

/**
 * In-app Capacitor plugin `RingerMode`
 * (frontend/native/android/RingerModePlugin.java + ios/App/App/RingerModePlugin.swift).
 *
 * Android: reports AudioManager ringer mode + Do Not Disturb. iOS: there is no
 * public mute-switch API, so state is always `unknown`; the plugin instead puts
 * the shared AVAudioSession in `.ambient` around chimes so the mute switch
 * silences them. Web: the browser cannot know — always `unknown`.
 */
type RingerModePluginApi = {
  getRingerState(): Promise<unknown>
  prepareChimeSession(options: { callActive: boolean }): Promise<{ ambient?: boolean }>
  releaseChimeSession(): Promise<void>
  addListener(
    eventName: 'ringerStateChanged',
    listener: (state: unknown) => void,
  ): Promise<{ remove: () => Promise<void> }>
}

const RingerMode = registerPlugin<RingerModePluginApi>('RingerMode')

/** Native read is ~ms; bound it so a wedged bridge can never delay an alert. */
const READ_TIMEOUT_MS = 400

let cached: RingerState = UNKNOWN_RINGER_STATE
const subscribers = new Set<(state: RingerState) => void>()
let nativeListenerStarted = false

function remember(state: RingerState): void {
  cached = state
  for (const cb of subscribers) {
    try {
      cb(state)
    } catch {
      /* subscriber errors never break the listener */
    }
  }
}

function ensureNativeListener(): void {
  if (nativeListenerStarted || getNativePlatform() !== 'android') return
  nativeListenerStarted = true
  void RingerMode.addListener('ringerStateChanged', (raw) => remember(normalizeRingerState(raw))).catch(
    () => {
      nativeListenerStarted = false
    },
  )
}

/** Last state seen (listener + reads). Synchronous; `unknown` until first read. */
export function getCachedRingerState(): RingerState {
  return cached
}

/**
 * Fresh ringer state. Android reads the native plugin (bounded by a short
 * timeout, falling back to the cached value); web and iOS resolve `unknown`.
 */
export async function getRingerState(): Promise<RingerState> {
  if (getNativePlatform() !== 'android') return UNKNOWN_RINGER_STATE
  ensureNativeListener()
  try {
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<RingerState>((resolve) => {
      timer = setTimeout(() => resolve(cached), READ_TIMEOUT_MS)
    })
    try {
      const state = await Promise.race([
        RingerMode.getRingerState().then(normalizeRingerState),
        timeout,
      ])
      cached = state
      return state
    } finally {
      clearTimeout(timer)
    }
  } catch {
    return cached
  }
}

/** Subscribe to ringer-state changes (Android). Returns an unsubscribe. */
export function subscribeRingerState(cb: (state: RingerState) => void): () => void {
  subscribers.add(cb)
  ensureNativeListener()
  return () => {
    subscribers.delete(cb)
  }
}

// ---------------------------------------------------------------------------
// iOS: chimes through an `.ambient` audio session so the mute switch silences them
// ---------------------------------------------------------------------------
//
// Ownership rules (the session is shared with call audio, so be conservative):
//  - Only ONE category change is in flight at a time (promise chain) — a
//    prepare and a release can never interleave.
//  - Never switch while a call is non-terminal, or while other app media
//    (voice-note playback, video) is playing — a category change would make it
//    go quiet mid-play. In those cases the chime plays on the unchanged session.
//  - The session is handed back when the last chime ends, and FORCE-released
//    (cancelling any pending restore) when: a ringtone / dial tone / hold music
//    claims the session, a call enters any non-terminal phase, other media
//    starts playing, a chime never plays (tryPlay gave up), or after a 4 s JS
//    failsafe. The native plugin has its own 4 s failsafe as a second net.

/** JS failsafe: longest chime is ~2 s; never stay ambient longer than this. */
export const CHIME_SESSION_FAILSAFE_MS = 4000

export type ChimeSessionResult =
  /** Session is `.ambient` — play the chime now (the mute switch will silence it). */
  | 'ambient'
  /** Session left unchanged (call / other media / not iOS) — play the chime as-is. */
  | 'plain'
  /** Ownership was taken away while waiting (call or ringtone started) — skip the chime. */
  | 'cancelled'

type WebAudioSession = { type: string }
function webAudioSession(): WebAudioSession | null {
  if (typeof navigator === 'undefined') return null
  const session = (navigator as unknown as { audioSession?: WebAudioSession }).audioSession
  return session && typeof session.type === 'string' ? session : null
}

function setWebAudioSessionType(type: 'ambient' | 'auto'): void {
  const web = webAudioSession()
  if (!web) return
  try {
    web.type = type
  } catch {
    /* unsupported value */
  }
}

/** True while a voice/video call is live (any non-terminal phase). */
export function isCallActiveNow(): boolean {
  const call = getVoiceCallSession()
  return !!call && !isTerminalCallPhase(call.phase)
}

/**
 * True when some OTHER app media element (voice-note playback, a video) is
 * playing. Elements that are muted (camera previews) or already ended don't
 * count. The app's own tone/chime elements are detached from the DOM so they
 * never show up here.
 */
export function isOtherMediaPlaying(): boolean {
  if (typeof document === 'undefined') return false
  const elements = document.querySelectorAll<HTMLMediaElement>('audio, video')
  for (const el of elements) {
    if (!el.paused && !el.ended && !el.muted && el.volume > 0) return true
  }
  return false
}

// Serialized native category changes. Each op is raced against a timeout so a
// native call that never resolves can't wedge the chain and block every later
// release (a stuck `.ambient` session would silence ringtones / ringback).
/** Max time one queued native op may hold the chain. */
export const SESSION_OP_TIMEOUT_MS = 1500

let opChain: Promise<unknown> = Promise.resolve()
function enqueue<T>(op: () => Promise<T>, fallback: T): Promise<T> {
  const guarded = async (): Promise<T> => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<T>((resolve) => {
      timer = setTimeout(() => resolve(fallback), SESSION_OP_TIMEOUT_MS)
    })
    try {
      return await Promise.race([op().catch(() => fallback), timeout])
    } finally {
      clearTimeout(timer)
    }
  }
  const run = opChain.then(guarded, guarded)
  opChain = run.then(
    () => undefined,
    () => undefined,
  )
  return run
}

let sessionActive = false
let activeChimes = 0
/** Bumped whenever ownership is revoked; a queued prepare that sees a newer epoch bails out. */
let epoch = 0
let failsafeTimer: ReturnType<typeof setTimeout> | null = null
let unsubscribeCall: (() => void) | null = null
let removeMediaWatch: (() => void) | null = null

function stopGuards(): void {
  if (failsafeTimer) {
    clearTimeout(failsafeTimer)
    failsafeTimer = null
  }
  unsubscribeCall?.()
  unsubscribeCall = null
  removeMediaWatch?.()
  removeMediaWatch = null
}

function startGuards(): void {
  // (Re)arm the failsafe on every chime.
  if (failsafeTimer) clearTimeout(failsafeTimer)
  failsafeTimer = setTimeout(() => {
    failsafeTimer = null
    void claimAudioSession()
  }, CHIME_SESSION_FAILSAFE_MS)

  if (!unsubscribeCall) {
    // Any call phase (incoming ring, outgoing dial, connecting, active) takes the session.
    unsubscribeCall = subscribeVoiceCallSession(() => {
      if (isCallActiveNow()) void claimAudioSession()
    })
  }
  if (!removeMediaWatch && typeof document !== 'undefined') {
    // Other media starting mid-chime must not be dragged onto an ambient session.
    const onPlay = (event: Event) => {
      const target = event.target as HTMLMediaElement | null
      if (target && !target.muted) void claimAudioSession()
    }
    document.addEventListener('play', onPlay, true)
    removeMediaWatch = () => document.removeEventListener('play', onPlay, true)
  }
}

/**
 * iOS only: before a chime plays, mark the web audio session `ambient`
 * (Safari 16.4+ Audio Session API) and have the native plugin switch the shared
 * AVAudioSession to `.ambient` — both are silenced by the mute switch.
 * Resolves with what the caller should do (see {@link ChimeSessionResult});
 * never rejects — a plugin failure just means the chime plays as before.
 */
export function beginChimeAudioSession(callActive: boolean): Promise<ChimeSessionResult> {
  if (getNativePlatform() !== 'ios') return Promise.resolve('plain')
  if (callActive || isCallActiveNow() || isOtherMediaPlaying()) return Promise.resolve('plain')

  const mine = epoch
  sessionActive = true
  activeChimes += 1
  startGuards()
  return enqueue(async (): Promise<ChimeSessionResult> => {
    if (epoch !== mine) return 'cancelled'
    setWebAudioSessionType('ambient')
    try {
      await RingerMode.prepareChimeSession({ callActive: false })
    } catch {
      // Plugin unavailable / rejected → session was not switched: play as-is.
      return epoch === mine ? 'plain' : 'cancelled'
    }
    return epoch === mine ? 'ambient' : 'cancelled'
  }, 'plain').then((result) => {
    // A failed / timed-out prepare means nobody will hand the session back via
    // the normal chime-end path ('plain' = nothing to hand back) — reset our
    // state and queue a (harmless, idempotent) native release in case the
    // hung prepare lands late.
    if (result === 'plain' && epoch === mine) void claimAudioSession()
    return result
  })
}

/** Opaque ownership ticket; pass to {@link endChimeAudioSession} so a stale chime can't end a newer session. */
export function getChimeSessionTicket(): number {
  return epoch
}

/**
 * One chime finished (ended / errored / gave up). Hands the session back once
 * the LAST outstanding chime is done. No-op when no chime session is active.
 */
export function endChimeAudioSession(ticket?: number): void {
  // A chime that began before ownership was revoked must not decrement a NEWER session.
  if (ticket !== undefined && ticket !== epoch) return
  if (!sessionActive) return
  activeChimes = Math.max(0, activeChimes - 1)
  if (activeChimes > 0) return
  void claimAudioSession()
}

/**
 * Take the audio session back from any chime NOW: cancels the failsafe, revokes
 * pending prepares, and restores the previous category (the native side cancels
 * its own pending restore and restores exactly once). Called by ringtone / dial
 * tone / hold-music start, a call entering any non-terminal phase, other media
 * starting, a chime that never played, and the failsafe timer. Returns the
 * in-flight release (or null when nothing was active) so callers/tests can await it.
 */
export function claimAudioSession(): Promise<void> | null {
  if (!sessionActive) return null
  sessionActive = false
  activeChimes = 0
  epoch += 1
  stopGuards()
  setWebAudioSessionType('auto')
  return enqueue(async () => {
    try {
      await RingerMode.releaseChimeSession()
    } catch {
      /* plugin unavailable */
    }
  }, undefined)
}
